/**
 * LoadSEER API Validation Utilities
 */

import type { ValidationResult } from '../types';

/**
 * Validates a UUID string format
 */
export function validateUUID(uuid: string): ValidationResult {
  if (!uuid || typeof uuid !== 'string') {
    return { valid: false, error: 'UUID is required' };
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(uuid)) {
    return { valid: false, error: 'Invalid UUID format' };
  }

  return { valid: true };
}

/**
 * Validates a LoadSEER API base URL
 * Accepts both full URLs (https://...) and relative paths (/loadseer-proxy/api)
 */
export function validateApiBaseUrl(url: string): ValidationResult {
  if (!url || typeof url !== 'string') {
    return { valid: false, error: 'API base URL is required' };
  }

  const trimmedUrl = url.trim();

  // Security: Reject protocol-relative URLs
  if (trimmedUrl.startsWith('//')) {
    return { valid: false, error: 'Protocol-relative URLs not allowed' };
  }

  // Security: Enforce maximum URL length
  if (trimmedUrl.length > 2048) {
    return { valid: false, error: 'URL exceeds maximum length (2048 characters)' };
  }

  // Accept relative paths (for Vite proxy)
  if (trimmedUrl.startsWith('/')) {
    return { valid: true };
  }

  // Validate full URLs
  try {
    const parsedUrl = new URL(trimmedUrl);

    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return { valid: false, error: 'API URL must use HTTP or HTTPS protocol' };
    }

    return { valid: true };
  } catch {
    return { valid: false, error: 'Invalid URL format' };
  }
}

/**
 * Validates an API token
 */
export function validateApiToken(token: string): ValidationResult {
  if (!token || typeof token !== 'string') {
    return { valid: false, error: 'API token is required' };
  }

  if (token.trim().length === 0) {
    return { valid: false, error: 'API token cannot be empty' };
  }

  // Security: Enforce maximum token length
  if (token.length > 4096) {
    return { valid: false, error: 'API token exceeds maximum length (4096 characters)' };
  }

  return { valid: true };
}

/**
 * Validates a node type code
 */
export function validateNodeTypeCode(code: string): ValidationResult {
  if (!code || typeof code !== 'string') {
    return { valid: false, error: 'Node type code is required' };
  }

  // Node type codes are typically 2-4 uppercase letters
  const nodeTypeRegex = /^[A-Z]{2,4}$/;

  if (!nodeTypeRegex.test(code)) {
    return { valid: false, error: 'Invalid node type code format' };
  }

  return { valid: true };
}

/**
 * Validates pagination parameters
 */
export function validatePaginationParams(page?: number, pageSize?: number): ValidationResult {
  if (page !== undefined) {
    if (!Number.isInteger(page) || page < 1) {
      return { valid: false, error: 'Page number must be a positive integer' };
    }
  }

  if (pageSize !== undefined) {
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) {
      return { valid: false, error: 'Page size must be between 1 and 100' };
    }
  }

  return { valid: true };
}

/**
 * Sanitizes a search query string
 */
export function sanitizeSearchQuery(query: string): string {
  if (!query || typeof query !== 'string') {
    return '';
  }

  // Remove potentially dangerous characters while preserving valid search input
  return (
    query
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
      .replace(/[<>:"/\\|?*]/g, '') // Remove filesystem/XSS dangerous chars
      .replace(/\s+/g, ' ') // Collapse multiple spaces to single space
      .trim()
      .slice(0, 200)
  ); // Limit length
}
